import { useState } from "react";
import Button from "../../../components/button";
import ConfirmDialog, {
  ConfirmDialogType,
} from "../../../components/confirm-dialog";
import DatafieldSection from "../../../components/datafield-section";
import { WCTLoanDetail } from "../../../dto";
import { FieldLabel, LoanType } from "../../../models";
import { splitKeyWithSpace } from "../../../utils";
import { WriteOffLoanFields } from "../../personal-loan-details/types";

interface WCTLoanSummaryProps {
  loanId: string;
  loanDetails: WCTLoanDetail;
  onWriteOffBtnClick: (
    type: ConfirmDialogType,
    fieldData: WriteOffLoanFields
  ) => void;
}

const leftLabelsForLoanSummary: Record<
  Extract<LoanType, "WORKING_CAPITAL_TERM_LOAN">,
  FieldLabel[]
> = {
  WORKING_CAPITAL_TERM_LOAN: [
    {
      key: "entitySnapshot.snapshot.name",
      label: "Enterprise Name: ",
      formatMethod: (name) => splitKeyWithSpace(name),
    },
    { key: "loanType", label: "Loan Type :", formatMethod: splitKeyWithSpace },
    { key: "approvedAmount", label: "Approved Amount :" },
    { key: "irpa", label: "Interest Rate :", suffix: "% PA" },
    { key: "pf", label: "Processing Fee :", prefix: "Rs " },
  ],
};

const rightLabelsForLoanSummary: Record<
  Exclude<LoanType, "TERM_LOAN" | "RETAIL_OD" | "COMMERCIAL_OD">,
  FieldLabel[]
> = {
  WORKING_CAPITAL_TERM_LOAN: [
    { key: "pfPostGst", label: "Processing Fee With GST :", prefix: "Rs " },
    { key: "tdsDeducted", label: "TDS Deducted :", prefix: "Rs " },
    { key: "tenureInMonths", label: "Tenure (Months) :" },
    {
      key: "totalPrincipalOutstanding",
      label: "Principal O/s:",
      prefix: " Rs",
      formatMethod: (value: number) => Math.max(value, 0).toFixed(2),
    },
    {
      key: "totalInterestOutstanding",
      label: "Interest O/s:",
      prefix: " Rs",
      formatMethod: (value: number) => Math.max(value, 0).toFixed(2),
    },
  ],
};

export default function WCTLoanSummary({
  loanDetails,
  loanId,
  onWriteOffBtnClick,
}: WCTLoanSummaryProps) {
  const [isWriteOffModalActive, setIsWriteOffModalActive] = useState(false);

  const handleWriteOffSubmit = async (
    type: ConfirmDialogType,
    fieldData: { [x: string]: string }
  ) => {
    onWriteOffBtnClick(type, {
      writeOffDate: fieldData.writeOffDate,
      reason: fieldData.writeOffReason,
    });
  };

  if (loanDetails.loanType !== "WORKING_CAPITAL_TERM_LOAN") {
    return null;
  }

  return (
    <div>
      <div className="d-flex justify-content-end">
        <Button
          type="button"
          variant="blue-outline"
          onClick={() => setIsWriteOffModalActive(true)}
        >
          Write Off
        </Button>
      </div>
      <DatafieldSection
        title="Loan Summary"
        labels={[
          {
            items: leftLabelsForLoanSummary[loanDetails.loanType],
          },
          {
            items: rightLabelsForLoanSummary[loanDetails.loanType],
          },
        ]}
        data={loanDetails}
      />
      <ConfirmDialog
        active={isWriteOffModalActive}
        title="Write off loan"
        submitButtonText="Write off"
        cancelButtonText="Cancel"
        type="success"
        message={`Are you sure you want to write off this loan? Loan ID: ${loanId}`}
        afterSuccessMessage={`Loan ID: ${loanId} has been written off successfully`}
        customErrorMessage={"Please fill all the fields"}
        onCancel={() => setIsWriteOffModalActive(false)}
        onSubmit={handleWriteOffSubmit}
        inputFields={[
          {
            name: "writeOffDate",
            label: "Write off date",
            type: "date",
            required: true,
            placeholder: "Write off date",
          },
          {
            name: "writeOffReason",
            label: "Write off reason",
            type: "text",
            required: true,
            placeholder: "Write off reason",
          },
        ]}
      />
    </div>
  );
}
